/**
 * Multi-Agent Lead Routing Engine
 * 
 * Production-ready routing core that:
 * - Evaluates multiple agents per lead
 * - Distributes leads proportionally (weighted fairness)
 * - Automatically retries when agents reject
 * - Logs every decision with full transparency
 * 
 * This module is pure and isolated with no UI dependencies.
 */

import type { MockAgent } from '@/contexts/MockAgentsContext';
import type { AllocationWeights } from '@/contexts/AllocationWeightsContext';
import type { PricingRuleForRouting, AllocationBreakdown } from './leadRouter';
import { normalizeLeadTypeId } from './leads/leadTypeIds';
import type { AgentPacingScore } from './agentPoolSelector';
import type { GuardrailSummary, GuardrailInputs } from './executionGuardrails';
import { buildGuardrailSummary } from './executionGuardrails';

// Soft floor multiplier cap for delivery pressure (max 30% boost)
const SOFT_FLOOR_MULTIPLIER_CAP = 2.0;
const CONTRACT_PRESSURE_MULTIPLIER = 1.2;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

// --- Type Definitions ---

export interface IncomingLead {
  id: string;
  leadTypeId: string;
  leadTypeName: string;
  vertical: 'life_insurance' | 'mortgage';
  state: string;
  estimatedValue: number;
}

export interface EligibilityCheck {
  check: string;
  passed: boolean;
  reason?: string;
}

export interface EligibleAgent {
  agent: MockAgent;
  effectiveBid: number;
  eligibilityChecks: EligibilityCheck[];
  breakdown: AllocationBreakdown;
}

export interface IneligibleAgent {
  agent: MockAgent;
  eligibilityChecks: EligibilityCheck[];
  rejectionCode: RejectionCode;
  rejectionReason: string;
}

export interface AgentPickAttempt {
  agentId: string;
  agentName: string;
  probability: number;
  pickOrder: number;
  outcome: 'selected' | 'rejected' | 'skipped';
  rejectionCode?: RejectionCode;
  rejectionReason?: string;
}

export type RejectionCode =
  | 'INTAKE_PAUSED'
  | 'AGENT_INACTIVE'
  | 'VERTICAL_MISMATCH'
  | 'LEAD_TYPE_INACTIVE'
  | 'CONTRACT_LEAD_TYPE_MISMATCH'
  | 'INVALID_BID'
  | 'BID_BELOW_MIN_PRICE'
  | 'NOT_LICENSED'
  | 'DAILY_CAP_REACHED'
  | 'MONTHLY_CAP_REACHED'
  | 'UNSOLD_LEAD';

export interface MultiAgentRoutingSuccess {
  outcome: 'success';
  selectedAgentId: string;
  selectedAgentName: string;
  chargedAmount: number;
  priorityScore: number;
  breakdown: AllocationBreakdown;
  eligibleAgents: EligibleAgent[];
  ineligibleAgents: IneligibleAgent[];
  pickAttempts: AgentPickAttempt[];
  totalAgentsEvaluated: number;
  // Delivery pressure enforcement
  pressureBoostApplied?: boolean;
  pressureBoostValue?: number;
  // Execution guardrails
  guardrailSummary?: GuardrailSummary;
}

export interface MultiAgentRoutingFailure {
  outcome: 'unsold';
  reason: string;
  code: RejectionCode;
  eligibleAgents: EligibleAgent[];
  ineligibleAgents: IneligibleAgent[];
  pickAttempts: AgentPickAttempt[];
  totalAgentsEvaluated: number;
  // Execution guardrails
  guardrailSummary?: GuardrailSummary;
}

export type MultiAgentRoutingResult = MultiAgentRoutingSuccess | MultiAgentRoutingFailure;

// --- Tier Multipliers ---

// --- Helper: Calculate Priority Score Breakdown ---

interface BreakdownResult extends AllocationBreakdown {
  pressureBoostApplied?: boolean;
  pressureBoostValue?: number;
}

function calculateBreakdown(
  agent: MockAgent,
  weights: AllocationWeights,
  deliveryPressureScore?: number,
): BreakdownResult {
  const performanceRatio = agent.performanceScore / 100;
  const qualityBonus = performanceRatio * Math.max(weights.qualityWeight, 0) * 0.1;
  const baseScore = 1 + qualityBonus;
  
  // Apply delivery pressure soft floor modifier
  // finalScore = baseScore * (1 + deliveryPressureScore * softFloorMultiplier)
  let pressureBoostApplied = false;
  let pressureBoostValue = 0;
  let finalScore = baseScore;
  
  if (deliveryPressureScore && deliveryPressureScore > 0) {
    // Soft floor boost: capped at SOFT_FLOOR_MULTIPLIER_CAP (30%)
    pressureBoostValue = Math.min(deliveryPressureScore * SOFT_FLOOR_MULTIPLIER_CAP, SOFT_FLOOR_MULTIPLIER_CAP);
    finalScore = baseScore * (1 + pressureBoostValue);
    pressureBoostApplied = true;
  }
  
  return {
    bidScore: 0,
    qualityScore: Math.round(agent.performanceScore * 100) / 100,
    bidWeight: 0,
    qualityWeight: weights.qualityWeight,
    finalScore: Math.round(finalScore * 100) / 100,
    pressureBoostApplied,
    pressureBoostValue: Math.round(pressureBoostValue * 1000) / 1000,
  };
}

// --- Helper: Eligibility Check ---

export interface EligibilityContext {
  getPricingRule?: (leadTypeId: string, state: string) => PricingRuleForRouting | null;
  checkIntake?: (vertical: 'life_insurance' | 'mortgage') => string | null;
  // Agent pacing scores for delivery pressure enforcement
  agentPacingScores?: Map<string, AgentPacingScore>;
  // Execution guardrails from Delivery Control
  agentGuardrails?: Map<string, GuardrailInputs>;
  // DEV overrides: agents ignoring contract caps
  agentsIgnoringCaps?: Set<string>;
  // DEV overrides: agents ignoring pricing rules
  agentsIgnoringPricing?: Set<string>;
  // Contract-level lead type restrictions: agentId → lead_types arrays from active contracts
  agentContractLeadTypes?: Map<string, string[][]>;
}

function evaluateAgentEligibility(
  agent: MockAgent,
  lead: IncomingLead,
  context: EligibilityContext
): { eligible: boolean; checks: EligibilityCheck[]; rejectionCode?: RejectionCode; rejectionReason?: string } {
  const checks: EligibilityCheck[] = [];
  
  // Check 0: Agent Active
  const agentActive = agent.isActive !== false;
  checks.push({
    check: 'Agent Active',
    passed: agentActive,
    reason: !agentActive ? 'Agent is inactive' : undefined,
  });
  if (!agentActive) {
    return { eligible: false, checks, rejectionCode: 'AGENT_INACTIVE', rejectionReason: 'Agent inactive' };
  }
  
  // Check 0: Vertical Compatibility (must match)
  const verticalMatch = agent.vertical === lead.vertical;
  checks.push({
    check: 'Vertical Match',
    passed: verticalMatch,
    reason: !verticalMatch ? `Agent vertical ${agent.vertical} != Lead vertical ${lead.vertical}` : undefined,
  });
  if (!verticalMatch) {
    return { eligible: false, checks, rejectionCode: 'VERTICAL_MISMATCH', rejectionReason: 'Agent vertical mismatch' };
  }
  
  // Check 1: System Intake
  if (context.checkIntake) {
    const intakeRejection = context.checkIntake(lead.vertical);
    const passed = !intakeRejection;
    checks.push({
      check: 'Intake Active',
      passed,
      reason: intakeRejection || undefined,
    });
    if (!passed) {
      return { eligible: false, checks, rejectionCode: 'INTAKE_PAUSED', rejectionReason: intakeRejection || 'Intake paused' };
    }
  } else {
    checks.push({ check: 'Intake Active', passed: true });
  }
  
  // Check 2: Lead Type Active
  const canonicalLeadTypeId = normalizeLeadTypeId(lead.leadTypeId);
  const pref = agent.leadPreferences.find(
    p => normalizeLeadTypeId(p.leadTypeId) === canonicalLeadTypeId
  );
  const leadTypeActive = pref?.isActive === true;
  checks.push({
    check: 'Lead Type Active',
    passed: leadTypeActive,
    reason: !leadTypeActive ? 'Lead type not active in preferences' : undefined,
  });
  if (!leadTypeActive) {
    return { eligible: false, checks, rejectionCode: 'LEAD_TYPE_INACTIVE', rejectionReason: 'Lead type not active' };
  }
  
  // Check 3: Pricing Rules (Admin Floor)
  // NOTE: Pricing rules are tracked for observability ONLY.
  // They do NOT block delivery in the current MVP.
  // Skip if agent is set to ignore pricing rules (DEV override)
  const ignorePricing = context.agentsIgnoringPricing?.has(agent.id) ?? false;
  if (ignorePricing) {
    checks.push({ check: 'Pricing Floor Tracked', passed: true, reason: 'DEV: Ignoring pricing floor' });
  } else if (context.getPricingRule) {
    const pricingRule = context.getPricingRule(canonicalLeadTypeId, lead.state);
    if (pricingRule && pricingRule.active) {
      checks.push({
        check: 'Pricing Floor Tracked',
        passed: true,
        reason: `Floor $${pricingRule.basePrice} (not enforced)`,
      });
    } else {
      checks.push({ check: 'Pricing Floor Tracked', passed: true, reason: 'No active floor' });
    }
  } else {
    checks.push({ check: 'Pricing Floor Tracked', passed: true, reason: 'No pricing rules configured' });
  }
  
  // Check 5: Licensed in State
  const isLicensed = agent.licensedStates.includes(lead.state);
  checks.push({
    check: 'Licensed in State',
    passed: isLicensed,
    reason: !isLicensed ? `Not licensed in ${lead.state}` : undefined,
  });
  if (!isLicensed) {
    return { eligible: false, checks, rejectionCode: 'NOT_LICENSED', rejectionReason: `Not licensed in ${lead.state}` };
  }

  // Check 5b: Contract Lead Types
  // If agent has contracts with explicit lead_types, at least one must cover this lead type.
  // Backwards compatible: no contract data or no lead_types set → passes.
  if (context.agentContractLeadTypes) {
    const contractLeadTypeSets = context.agentContractLeadTypes.get(agent.id);
    if (contractLeadTypeSets && contractLeadTypeSets.length > 0) {
      const hasMatchingContract = contractLeadTypeSets.some(
        (types) => types.includes(canonicalLeadTypeId)
      );
      checks.push({
        check: 'Contract Lead Type',
        passed: hasMatchingContract,
        reason: !hasMatchingContract ? 'No active contract covers this lead type' : undefined,
      });
      if (!hasMatchingContract) {
        return { eligible: false, checks, rejectionCode: 'CONTRACT_LEAD_TYPE_MISMATCH', rejectionReason: 'No active contract covers this lead type' };
      }
    } else {
      checks.push({ check: 'Contract Lead Type', passed: true, reason: 'No contract restrictions' });
    }
  }

  // Check 6: Daily Cap
  // Skip if agent is set to ignore contract caps (DEV override)
  const ignoreCaps = context.agentsIgnoringCaps?.has(agent.id) ?? false;
  if (ignoreCaps) {
    checks.push({ check: 'Daily Cap Available', passed: true, reason: 'DEV: Ignoring caps' });
    checks.push({ check: 'Monthly Cap Available', passed: true, reason: 'DEV: Ignoring caps' });
  } else {
    const dailyCap = pref?.dailyCap ?? 0;
    const dailyAvailable = agent.leadsReceivedToday < dailyCap;
    checks.push({
      check: 'Daily Cap Available',
      passed: dailyAvailable,
      reason: !dailyAvailable ? `${agent.leadsReceivedToday}/${dailyCap} used` : undefined,
    });
    if (!dailyAvailable) {
      return { eligible: false, checks, rejectionCode: 'DAILY_CAP_REACHED', rejectionReason: 'Daily cap reached' };
    }
    
    // Check 7: Monthly Cap
    const monthlyCap = pref?.monthlyCap ?? 0;
    const monthlyAvailable = agent.leadsReceivedThisMonth < monthlyCap;
    checks.push({
      check: 'Monthly Cap Available',
      passed: monthlyAvailable,
      reason: !monthlyAvailable ? `${agent.leadsReceivedThisMonth}/${monthlyCap} used` : undefined,
    });
    if (!monthlyAvailable) {
      return { eligible: false, checks, rejectionCode: 'MONTHLY_CAP_REACHED', rejectionReason: 'Monthly cap reached' };
    }
  }
  
  return { eligible: true, checks };
}

// --- Step 1: Get Eligible Agent Pool ---

export function getEligibleAgentPool(
  lead: IncomingLead,
  agents: MockAgent[],
  weights: AllocationWeights,
  context: EligibilityContext = {}
): { eligible: EligibleAgent[]; ineligible: IneligibleAgent[]; pressureBoostApplied: boolean; guardrailsApplied: boolean } {
  const eligible: EligibleAgent[] = [];
  const ineligible: IneligibleAgent[] = [];
  let anyPressureBoostApplied = false;
  let anyGuardrailsApplied = false;
  
  for (const agent of agents) {
    const result = evaluateAgentEligibility(agent, lead, context);
    if (result.eligible) {
      
      // Guardrail pacing caps were previously applied to bid values.
      // Bids are no longer part of routing for MVP, so we skip bid-based caps here.
      
      // Get delivery pressure score from context if available
      const pacingScore = context.agentPacingScores?.get(agent.id);
      const deliveryPressureScore = pacingScore?.deliveryPressureScore ?? 0;
      const contractPressureScore = pacingScore?.contractPressureScore ?? 0;
      const combinedPressureScore = clamp(
        deliveryPressureScore + (contractPressureScore * CONTRACT_PRESSURE_MULTIPLIER),
        0,
        1
      );
      
      const breakdown = calculateBreakdown(
        agent,
        weights,
        combinedPressureScore
      );
      
      if (breakdown.pressureBoostApplied) {
        anyPressureBoostApplied = true;
      }
      
      eligible.push({
        agent,
        effectiveBid: 0,
        eligibilityChecks: result.checks,
        breakdown,
      });
    } else {
      ineligible.push({
        agent,
        eligibilityChecks: result.checks,
        rejectionCode: result.rejectionCode!,
        rejectionReason: result.rejectionReason!,
      });
    }
  }
  
  // Sort eligible by finalScore descending
  eligible.sort((a, b) => b.breakdown.finalScore - a.breakdown.finalScore);
  
  return { eligible, ineligible, pressureBoostApplied: anyPressureBoostApplied, guardrailsApplied: anyGuardrailsApplied };
}

// --- Step 3: Weighted Random Selection ---

/**
 * Weighted random pick utility.
 * Uses finalScore as probability weight.
 * All eligible agents have a non-zero chance proportional to their score.
 */
export function weightedRandomPick<T extends { breakdown: AllocationBreakdown }>(
  candidates: T[],
  random: () => number = Math.random
): { selected: T; probability: number } | null {
  if (candidates.length === 0) return null;
  if (candidates.length === 1) {
    return { selected: candidates[0], probability: 1 };
  }
  
  // Calculate total score
  const totalScore = candidates.reduce((sum, c) => sum + Math.max(c.breakdown.finalScore, 0.01), 0);
  
  // Generate random threshold
  const threshold = random() * totalScore;
  
  // Select agent based on weighted probability
  let cumulative = 0;
  for (const candidate of candidates) {
    const score = Math.max(candidate.breakdown.finalScore, 0.01);
    cumulative += score;
    if (cumulative >= threshold) {
      return {
        selected: candidate,
        probability: Math.round((score / totalScore) * 10000) / 100, // percentage with 2 decimals
      };
    }
  }
  
  // Fallback to first (highest scorer)
  const fallbackScore = Math.max(candidates[0].breakdown.finalScore, 0.01);
  return {
    selected: candidates[0],
    probability: Math.round((fallbackScore / totalScore) * 10000) / 100,
  };
}

// --- Step 4 & 5: Full Routing with Fallback Loop ---

/**
 * Route a lead to the best available agent using weighted fairness.
 * 
 * Flow:
 * 1. Evaluate all agents for eligibility
 * 2. Score eligible agents
 * 3. Pick agent via weighted random selection
 * 4. If rejected (e.g., cap reached during pick), remove from pool and retry
 * 5. Repeat until accepted or pool exhausted
 * 6. Return success with agent details or UNSOLD_LEAD outcome
 */
export function routeLeadMultiAgent(
  lead: IncomingLead,
  agents: MockAgent[],
  weights: AllocationWeights,
  context: EligibilityContext = {},
  random: () => number = Math.random
): MultiAgentRoutingResult {
  // Step 1: Get eligible pool
  const { eligible, ineligible, guardrailsApplied } = getEligibleAgentPool(lead, agents, weights, context);
  
  // Build guardrail summary if any guardrails were active
  let overallGuardrailSummary: GuardrailSummary | undefined;
  if (context.agentGuardrails && guardrailsApplied) {
    const guardrailReasons: string[] = [];
    const agentsWithCaps = Array.from(context.agentGuardrails.entries())
      .filter(([, g]) => g.pacingCap < 1.0);
    
    if (agentsWithCaps.length > 0) {
      guardrailReasons.push(`Pacing caps applied to ${agentsWithCaps.length} agent(s)`);
    }
    
    overallGuardrailSummary = {
      guardrailsApplied: true,
      guardrailReasons,
      appliedGuardrails: [],
    };
  }
  
  const pickAttempts: AgentPickAttempt[] = [];
  let remainingPool = [...eligible];
  let pickOrder = 0;
  
  // Log all ineligible agents as "skipped" attempts
  for (const ia of ineligible) {
    pickAttempts.push({
      agentId: ia.agent.id,
      agentName: ia.agent.name,
      probability: 0,
      pickOrder: -1, // Not considered for picking
      outcome: 'skipped',
      rejectionCode: ia.rejectionCode,
      rejectionReason: ia.rejectionReason,
    });
  }
  
  // Step 4: Fallback loop
  while (remainingPool.length > 0) {
    pickOrder++;
    
    // Step 3: Weighted random pick
    const pickResult = weightedRandomPick(remainingPool, random);
    if (!pickResult) break;
    
    const { selected, probability } = pickResult;
    
    // In a real system, this is where we'd do the actual allocation attempt
    // which could fail due to race conditions. For mock, we accept immediately.
    
    // Record successful pick
    pickAttempts.push({
      agentId: selected.agent.id,
      agentName: selected.agent.name,
      probability,
      pickOrder,
      outcome: 'selected',
    });
    
    // Mark remaining agents as not picked
    for (const remaining of remainingPool) {
      if (remaining.agent.id !== selected.agent.id) {
        const totalScore = remainingPool.reduce((sum, c) => sum + Math.max(c.breakdown.finalScore, 0.01), 0);
        const prob = Math.round((Math.max(remaining.breakdown.finalScore, 0.01) / totalScore) * 10000) / 100;
        pickAttempts.push({
          agentId: remaining.agent.id,
          agentName: remaining.agent.name,
          probability: prob,
          pickOrder,
          outcome: 'skipped',
          rejectionReason: 'Not selected in weighted pick',
        });
      }
    }
    
    // Get pressure boost value for selected agent
    const selectedBreakdown = selected.breakdown as BreakdownResult;
    
    // Build agent-specific guardrail summary for the winner
    const selectedAgentGuardrails = context.agentGuardrails?.get(selected.agent.id);
    if (selectedAgentGuardrails) {
      const agentSummary = buildGuardrailSummary(selected.agent.id, selectedAgentGuardrails);
      if (agentSummary.guardrailsApplied) {
        overallGuardrailSummary = {
          ...overallGuardrailSummary,
          ...agentSummary,
          guardrailsApplied: true,
        };
      }
    }
    
    // Return success
    return {
      outcome: 'success',
      selectedAgentId: selected.agent.id,
      selectedAgentName: selected.agent.name,
      chargedAmount: selected.effectiveBid,
      priorityScore: selected.breakdown.finalScore,
      breakdown: selected.breakdown,
      eligibleAgents: eligible,
      ineligibleAgents: ineligible,
      pickAttempts,
      totalAgentsEvaluated: agents.length,
      pressureBoostApplied: selectedBreakdown.pressureBoostApplied,
      pressureBoostValue: selectedBreakdown.pressureBoostValue,
      guardrailSummary: overallGuardrailSummary,
    };
  }
  
  // Step 5: No agents available - UNSOLD_LEAD
  return {
    outcome: 'unsold',
    reason: eligible.length === 0 
      ? 'No eligible agents found for this lead'
      : 'All eligible agents rejected the lead',
    code: 'UNSOLD_LEAD',
    eligibleAgents: eligible,
    ineligibleAgents: ineligible,
    pickAttempts,
    totalAgentsEvaluated: agents.length,
    guardrailSummary: overallGuardrailSummary,
  };
}

// --- Utility: Calculate All Agent Probabilities ---

/**
 * Calculate selection probabilities for all eligible agents.
 * Useful for displaying distribution before actual pick.
 */
export function calculateAgentProbabilities(
  eligibleAgents: EligibleAgent[]
): Array<{ agentId: string; agentName: string; probability: number; score: number }> {
  if (eligibleAgents.length === 0) return [];
  
  const totalScore = eligibleAgents.reduce(
    (sum, ea) => sum + Math.max(ea.breakdown.finalScore, 0.01),
    0
  );
  
  return eligibleAgents.map((ea) => {
    const score = Math.max(ea.breakdown.finalScore, 0.01);
    return {
      agentId: ea.agent.id,
      agentName: ea.agent.name,
      probability: Math.round((score / totalScore) * 10000) / 100,
      score: ea.breakdown.finalScore,
    };
  });
}

// --- Debug Helper ---

export function explainRoutingDecision(result: MultiAgentRoutingResult): string {
  if (result.outcome === 'success') {
    return `Lead routed to ${result.selectedAgentName} (${result.selectedAgentId}). ` +
      `Charged: $${result.chargedAmount}, Priority Score: ${result.priorityScore.toFixed(2)}. ` +
      `${result.eligibleAgents.length} eligible, ${result.ineligibleAgents.length} ineligible of ${result.totalAgentsEvaluated} total.`;
  } else {
    return `Lead unsold: ${result.reason}. ` +
      `${result.eligibleAgents.length} eligible, ${result.ineligibleAgents.length} ineligible of ${result.totalAgentsEvaluated} total.`;
  }
}
